import { Leena2ProductImpactReasoning } from '@upright/common/model/output/scores/drilldowns/leena2-impact-reasoning-types';
import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { useQuery } from '@tanstack/react-query';

/**
 * Fetches product-impact reasoning (explanation + pathway diagram + scientific
 * sources) for one product and one free-form leaf impact, via the team-only
 * `POST /api/leena2/product-impact-reasoning` endpoint. Used by the Top-impacts
 * detail dialog for the currently-selected product.
 *
 * First request for a given (product, impact) pair triggers an LLM generation +
 * web search server-side (cached thereafter), so callers should show a loading
 * state. `enabled` lets the dialog defer the call until a product is selected.
 *
 * PLT-5685: `includeArticles` drives the two-stage load. The reasoning text +
 * pathway come from one fast LLM call; the scientific-article search is a slow
 * web-search pipeline. Callers fetch the reasoning first with
 * `includeArticles: false`, then fetch articles with `includeArticles: true`
 * (gated on the first call having succeeded so the reasoning is already cached,
 * making the second call pay only the article cost). It is part of the query
 * key so the two calls are cached independently. Defaults to true, preserving
 * the all-in-one fetch for any caller that omits it.
 */
export function useProductImpactReasoning({
  productCode,
  productDescription,
  impactDescription,
  includeArticles = true,
  enabled,
}: {
  productCode: string | undefined;
  productDescription?: string;
  impactDescription: string | undefined;
  includeArticles?: boolean;
  enabled: boolean;
}) {
  return useQuery({
    queryKey: ['queryProductImpactReasoning', productCode, productDescription, impactDescription, includeArticles],

    queryFn: async () =>
      await fetchOrThrow<Leena2ProductImpactReasoning>('/leena2/product-impact-reasoning', 'POST', {
        productCode,
        productDescription,
        impactDescription,
        includeArticles,
      }),

    enabled:
      enabled && impactDescription !== undefined && (productCode !== undefined || productDescription !== undefined),
    refetchOnWindowFocus: false,
    retry: false,
    // Reasoning is cached server-side and stable; keep it fresh for the session.
    staleTime: Infinity,
  });
}
